import React from "react";
import { Button } from "@/components/ui/button";
import { LayoutGrid, Table } from "lucide-react";
import IconProvider from "@/components/ui/IconProvider";
import { ViewMode } from "../types";

export interface ViewModeToggleProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  allowFlowView?: boolean; // Whether to show flow view option
}

/**
 * View mode toggle component.
 * Toggles between grid, table, and optionally flow view modes.
 */
export function ViewModeToggle({
  viewMode,
  onViewModeChange,
  allowFlowView = false,
}: ViewModeToggleProps) {
  const handleToggle = () => {
    if (allowFlowView) {
      // Cycle through grid -> table -> flow -> grid
      if (viewMode === "grid") {
        onViewModeChange("table");
      } else if (viewMode === "table") {
        onViewModeChange("flow");
      } else {
        onViewModeChange("grid");
      }
    } else {
      // Simple toggle between grid and table
      onViewModeChange(viewMode === "grid" ? "table" : "grid");
    }
  };

  const getIcon = () => {
    if (viewMode === "grid") {
      return <Table className="h-4 w-4" />;
    } else if (viewMode === "table") {
      return allowFlowView ? (
        <IconProvider iconstring="fa-project-diagram" size={16} />
      ) : (
        <LayoutGrid className="h-4 w-4" />
      );
    } else {
      return <LayoutGrid className="h-4 w-4" />;
    }
  };

  const getTitle = () => {
    if (viewMode === "grid") {
      return "Switch to Table View";
    } else if (viewMode === "table") {
      return allowFlowView ? "Switch to Flow View" : "Switch to Grid View";
    } else {
      return "Switch to Grid View";
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted"
      onClick={handleToggle}
      title={getTitle()}
    >
      {getIcon()}
    </Button>
  );
}

export default ViewModeToggle;

